import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Wrench, Droplets, Calculator, Settings, Plus, CheckCircle, LogOut, Building2, UserPlus, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';

const modulos = [
  {
    id: 'oficina',
    nome: 'Oficina Mecânica',
    descricao: 'Sistema completo de gestão para oficinas mecânicas',
    icone: Wrench,
    cor: 'from-blue-500 to-blue-700',
    corBorda: 'border-blue-500/30',
    status: 'online',
    rota: '/oficina/login'
  },
  {
    id: 'agua',
    nome: 'Loja de Água',
    descricao: 'Sistema de cartão fidelidade para distribuidora de água',
    icone: Droplets,
    cor: 'from-cyan-500 to-cyan-700',
    corBorda: 'border-cyan-500/30',
    status: 'online',
    rota: '/agua/login'
  },
  {
    id: 'financas',
    nome: 'Financeiro MEI/ME',
    descricao: 'Gestão financeira completa para pequenas empresas',
    icone: Calculator,
    cor: 'from-emerald-500 to-emerald-700',
    corBorda: 'border-emerald-500/30',
    status: 'online',
    rota: '/financas/login'
  }
];

function PainelCentral() {
  const navigate = useNavigate();
  const [ultimoAcesso, setUltimoAcesso] = useState({});
  const [usuarios, setUsuarios] = useState([]);
  const [empresasFinancas, setEmpresasFinancas] = useState([]);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isListDialogOpen, setIsListDialogOpen] = useState(false);
  const [isEmpresaDialogOpen, setIsEmpresaDialogOpen] = useState(false);
  const [selectedModulo, setSelectedModulo] = useState('');
  const [empresaSelecionada, setEmpresaSelecionada] = useState('');
  const [novaEmpresaNome, setNovaEmpresaNome] = useState('');
  const [formData, setFormData] = useState({ username: '', password: '', nome: '' });
  const [painelUser, setPainelUser] = useState(null);

  useEffect(() => {
    // Verificar se está logado
    const token = localStorage.getItem('painel_token');
    const user = localStorage.getItem('painel_user');
    if (!token) {
      navigate('/');
      return;
    }
    if (user) {
      setPainelUser(JSON.parse(user));
    }
    
    // Carregar último acesso
    const acessos = JSON.parse(localStorage.getItem('ultimosAcessos') || '{}');
    setUltimoAcesso(acessos);
    
    loadUsuarios();
    loadEmpresas();
  }, [navigate]);

  const loadUsuarios = async () => {
    try {
      const { data } = await api.get('/painel/usuarios');
      setUsuarios(data);
    } catch (error) {
      console.error(error);
    }
  };

  const loadEmpresas = async () => {
    try {
      const { data } = await api.get('/painel/financas/empresas');
      setEmpresasFinancas(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAbrirModulo = (modulo) => {
    // Salvar último acesso
    const acessos = JSON.parse(localStorage.getItem('ultimosAcessos') || '{}');
    acessos[modulo.id] = new Date().toISOString();
    localStorage.setItem('ultimosAcessos', JSON.stringify(acessos));
    
    navigate(modulo.rota);
  };

  const handleCriarUsuario = async (e) => {
    e.preventDefault();
    if (!selectedModulo) {
      toast.error('Selecione um módulo');
      return;
    }
    if (selectedModulo === 'financas' && !empresaSelecionada) {
      toast.error('Selecione ou crie uma empresa');
      return;
    }
    try {
      const payload = { modulo: selectedModulo, ...formData };
      if (selectedModulo === 'financas') payload.empresa_id = empresaSelecionada;
      await api.post('/painel/usuarios', payload);
      const moduloNome = selectedModulo === 'oficina' ? 'Oficina' : selectedModulo === 'agua' ? 'Loja de Água' : 'Financeiro';
      toast.success(`Usuário criado para ${moduloNome}!`);
      setIsUserDialogOpen(false);
      setFormData({ username: '', password: '', nome: '' });
      setSelectedModulo('');
      setEmpresaSelecionada('');
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const handleCriarEmpresa = async (e) => {
    e.preventDefault();
    if (!novaEmpresaNome.trim()) { toast.error('Nome obrigatório'); return; }
    try {
      const { data } = await api.post('/painel/financas/empresas', { nome: novaEmpresaNome.trim() });
      toast.success(`Empresa "${data.nome}" criada!`);
      setNovaEmpresaNome('');
      setIsEmpresaDialogOpen(false);
      await loadEmpresas();
      setEmpresaSelecionada(data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar empresa');
    }
  };

  const handleDeleteEmpresa = async (id, nome) => {
    if (!window.confirm(`Excluir a empresa "${nome}"? TODOS os dados financeiros e usuários serão removidos.`)) return;
    try {
      await api.delete(`/painel/financas/empresas/${id}`);
      toast.success('Empresa removida');
      loadEmpresas();
      loadUsuarios();
    } catch {
      toast.error('Erro ao excluir empresa');
    }
  };

  const handleDeleteUsuario = async (modulo, userId) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await api.delete(`/painel/usuarios/${modulo}/${userId}`);
        toast.success('Usuário excluído');
        loadUsuarios();
      } catch (error) {
        toast.error('Erro ao excluir usuário');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('painel_token');
    localStorage.removeItem('painel_user');
    navigate('/');
  };

  const formatarUltimoAcesso = (moduloId) => {
    const acesso = ultimoAcesso[moduloId];
    if (!acesso) return 'Nunca acessado';
    
    const data = new Date(acesso);
    return `Último acesso: ${data.toLocaleDateString('pt-BR')} às ${data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const getUsuariosPorModulo = (moduloId) => {
    return usuarios.filter(u => u.modulo === moduloId);
  };

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="bg-carbon border-b border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-electric-blue to-blue-700 rounded-lg flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white uppercase tracking-tight">Sistema de Gestão</h1>
              <p className="text-sm text-slate-500">Painel Central Modular</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm text-slate-400 hidden sm:block">Olá, {painelUser?.nome || 'Admin'}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsListDialogOpen(true)}
              className="text-slate-400 hover:text-white hover:bg-white/10"
            >
              <Users className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-slate-400 hover:text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-carbon border border-white/5 rounded-lg p-4">
            <p className="text-sm text-slate-500 uppercase tracking-wider">Módulos Instalados</p>
            <p className="text-3xl font-bold text-white mt-1">{modulos.length}</p>
          </div>
          <div className="bg-carbon border border-white/5 rounded-lg p-4">
            <p className="text-sm text-slate-500 uppercase tracking-wider">Status Geral</p>
            <div className="flex items-center space-x-2 mt-1">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <p className="text-lg font-semibold text-green-500">Todos Online</p>
            </div>
          </div>
          <div className="bg-carbon border border-white/5 rounded-lg p-4">
            <p className="text-sm text-slate-500 uppercase tracking-wider">Usuários Cadastrados</p>
            <p className="text-3xl font-bold text-white mt-1">{usuarios.length}</p>
          </div>
        </div>

        {/* Título da seção */}
        <div className="flex flex-wrap items-center justify-between mb-6 gap-3">
          <h2 className="text-xl font-bold text-white uppercase tracking-wide">Módulos Disponíveis</h2>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => navigate('/painel/empresas')}
              variant="outline"
              className="bg-transparent border-electric-blue/40 text-electric-blue hover:bg-electric-blue/10"
              data-testid="painel-empresas-btn"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Empresas
            </Button>
            <Button 
              onClick={() => setIsUserDialogOpen(true)}
              className="bg-electric-blue hover:bg-blue-600 text-white"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Criar Usuário
            </Button>
          </div>
        </div>

        {/* Cards dos Módulos */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modulos.map((modulo, index) => (
            <motion.div
              key={modulo.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`bg-carbon border ${modulo.corBorda} rounded-xl overflow-hidden hover:border-opacity-60 transition-all duration-300 group`}
            >
              {/* Header do Card */}
              <div className={`bg-gradient-to-r ${modulo.cor} p-6`}>
                <div className="flex items-center justify-between">
                  <modulo.icone className="w-12 h-12 text-white" />
                  <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-medium text-white uppercase">
                    {modulo.status}
                  </span>
                </div>
              </div>

              {/* Body do Card */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-2">{modulo.nome}</h3>
                <p className="text-sm text-slate-400 mb-2">{modulo.descricao}</p>
                <p className="text-xs text-slate-500 mb-2">{formatarUltimoAcesso(modulo.id)}</p>
                <p className="text-xs text-electric-blue mb-4">
                  {getUsuariosPorModulo(modulo.id).length} usuário(s) cadastrado(s)
                </p>
                
                <Button
                  onClick={() => handleAbrirModulo(modulo)}
                  className={`w-full bg-gradient-to-r ${modulo.cor} hover:opacity-90 text-white font-medium`}
                >
                  Abrir Sistema
                </Button>
              </div>
            </motion.div>
          ))}

          {/* Card para adicionar novo módulo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: modulos.length * 0.1 }}
            className="bg-carbon border border-dashed border-white/10 rounded-xl p-6 flex flex-col items-center justify-center min-h-[280px] hover:border-white/20 transition-colors cursor-not-allowed opacity-50"
          >
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-4">
              <Plus className="w-8 h-8 text-slate-500" />
            </div>
            <h3 className="text-lg font-medium text-slate-500 mb-2">Novo Módulo</h3>
            <p className="text-sm text-slate-600 text-center">Em breve você poderá adicionar novos módulos ao sistema</p>
          </motion.div>
        </div>
      </main>

      {/* Dialog Criar Usuário */}
      <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
        <DialogContent className="bg-carbon border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Criar Usuário para Módulo</DialogTitle>
            <DialogDescription className="text-slate-400">
              Selecione um módulo, defina credenciais e (no caso do Financeiro) escolha ou crie a empresa.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCriarUsuario} className="space-y-4">
            <div>
              <Label className="text-slate-400">Módulo</Label>
              <Select value={selectedModulo} onValueChange={(v) => { setSelectedModulo(v); setEmpresaSelecionada(''); }}>
                <SelectTrigger className="bg-obsidian border-white/10 text-white" data-testid="painel-modulo-select">
                  <SelectValue placeholder="Selecione o módulo" />
                </SelectTrigger>
                <SelectContent className="bg-carbon border-white/10 text-white">
                  <SelectItem value="oficina">Oficina Mecânica</SelectItem>
                  <SelectItem value="agua">Loja de Água</SelectItem>
                  <SelectItem value="financas">Financeiro MEI/ME</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selectedModulo === 'financas' && (
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-emerald-300">Empresa / Loja</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsEmpresaDialogOpen(true)}
                    className="text-emerald-300 hover:text-white hover:bg-emerald-500/10 h-7"
                    data-testid="painel-nova-empresa-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Nova Empresa
                  </Button>
                </div>
                <Select value={empresaSelecionada} onValueChange={setEmpresaSelecionada}>
                  <SelectTrigger className="bg-obsidian border-white/10 text-white" data-testid="painel-empresa-select">
                    <SelectValue placeholder="Selecione a empresa" />
                  </SelectTrigger>
                  <SelectContent className="bg-carbon border-white/10 text-white">
                    {empresasFinancas.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-400">Nenhuma empresa. Crie uma primeiro.</div>
                    ) : empresasFinancas.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-emerald-300/60 text-xs">Cada empresa tem seus próprios dados financeiros isolados.</p>
              </div>
            )}
            <div>
              <Label className="text-slate-400">Nome</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                className="bg-obsidian border-white/10 text-white"
                placeholder="Nome completo"
                required
                data-testid="painel-user-nome-input"
              />
            </div>
            <div>
              <Label className="text-slate-400">Usuário</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="bg-obsidian border-white/10 text-white"
                placeholder="Nome de usuário"
                required
                data-testid="painel-user-username-input"
              />
            </div>
            <div>
              <Label className="text-slate-400">Senha</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-obsidian border-white/10 text-white"
                placeholder="Senha"
                required
                data-testid="painel-user-password-input"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setIsUserDialogOpen(false)} className="text-slate-400">
                Cancelar
              </Button>
              <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white" data-testid="painel-criar-usuario-submit">
                Criar Usuário
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Lista de Usuários */}
      <Dialog open={isListDialogOpen} onOpenChange={setIsListDialogOpen}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Usuários Cadastrados</DialogTitle>
            <DialogDescription className="text-slate-400">
              Visualize, exclua usuários e gerencie empresas do módulo Financeiro.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {modulos.map(modulo => (
              <div key={modulo.id} className="space-y-2">
                <h3 className="text-sm font-semibold text-slate-400 uppercase flex items-center">
                  <modulo.icone className="w-4 h-4 mr-2" />
                  {modulo.nome}
                </h3>
                {getUsuariosPorModulo(modulo.id).length === 0 ? (
                  <p className="text-sm text-slate-500 pl-6">Nenhum usuário cadastrado</p>
                ) : (
                  <div className="space-y-2">
                    {getUsuariosPorModulo(modulo.id).map(user => (
                      <div key={user.id} className="flex items-center justify-between bg-obsidian p-3 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{user.nome}</p>
                          <p className="text-sm text-slate-500">@{user.username}{user.empresa_nome ? ` · ${user.empresa_nome}` : ''}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteUsuario(modulo.id, user.id)}
                          className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Empresas do Financeiro */}
            <div className="space-y-2 border-t border-white/5 pt-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-emerald-400 uppercase flex items-center">
                  <Building2 className="w-4 h-4 mr-2" />
                  Empresas (Financeiro)
                </h3>
                <Button size="sm" variant="ghost" onClick={() => setIsEmpresaDialogOpen(true)} className="text-emerald-300 hover:text-white hover:bg-emerald-500/10 h-7" data-testid="painel-list-nova-empresa-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Nova Empresa
                </Button>
              </div>
              {empresasFinancas.length === 0 ? (
                <p className="text-sm text-slate-500 pl-6">Nenhuma empresa cadastrada</p>
              ) : (
                <div className="space-y-2">
                  {empresasFinancas.map(emp => (
                    <div key={emp.id} className="flex items-center justify-between bg-obsidian p-3 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{emp.nome}</p>
                        <p className="text-xs text-slate-500">{emp.total_usuarios || 0} usuário(s){emp.cnpj ? ` · CNPJ: ${emp.cnpj}` : ''}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteEmpresa(emp.id, emp.nome)}
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                        data-testid={`painel-empresa-excluir-${emp.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Criar Empresa */}
      <Dialog open={isEmpresaDialogOpen} onOpenChange={setIsEmpresaDialogOpen}>
        <DialogContent className="bg-carbon border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Nova Empresa (Financeiro)</DialogTitle>
            <DialogDescription className="text-slate-400">
              Crie uma empresa isolada com seus próprios dados financeiros.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCriarEmpresa} className="space-y-4">
            <div>
              <Label className="text-slate-400">Nome da Empresa *</Label>
              <Input
                value={novaEmpresaNome}
                onChange={(e) => setNovaEmpresaNome(e.target.value)}
                className="bg-obsidian border-white/10 text-white"
                placeholder="Ex: Padaria do João"
                required
                autoFocus
                data-testid="painel-empresa-nome-input"
              />
              <p className="text-xs text-slate-500 mt-2">Cada empresa tem dados financeiros isolados. Você poderá criar usuários para esta empresa em seguida.</p>
            </div>
            <div className="flex justify-end space-x-3 pt-2">
              <Button type="button" variant="ghost" onClick={() => setIsEmpresaDialogOpen(false)} className="text-slate-400">Cancelar</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="painel-empresa-criar-submit">Criar Empresa</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-carbon border-t border-white/5 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <p className="text-xs text-slate-500">Sistema de Gestão Modular v1.0.0</p>
          <p className="text-xs text-slate-500">Todos os direitos reservados</p>
        </div>
      </footer>
    </div>
  );
}

export default PainelCentral;
